// src/components/VendorAutocomplete.js
// Searchable vendor field — type to search the vendors table or click a result.
// Stores the vendor name as plain text (mirrors how customer is stored).
//
// Used for chassis_vendor and body_vendor fields on inventory records.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../scripts/api";

export default function VendorAutocomplete({ label, value, onChange }) {
  const [query,   setQuery]   = useState(value || "");
  const [results, setResults] = useState([]);
  const [open,    setOpen]    = useState(false);
  const [loading, setLoading] = useState(false);
  const debounce = useRef(null);
  const wrapRef  = useRef(null);

  // Sync local query if parent resets value
  useEffect(() => { setQuery(value || ""); }, [value]);

  const search = useCallback((q) => {
    clearTimeout(debounce.current);
    if (!q.trim()) { setResults([]); setOpen(false); return; }
    debounce.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/vendors", { params: { q: q.trim(), limit: 12 } });
        setResults(data?.rows || []);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 220);
  }, []);

  const handleChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    onChange(v);
    search(v);
  };

  const select = (row) => {
    const name = row.vendor_name || row.name || row["ven_"] || "";
    setQuery(name);
    onChange(name);
    setResults([]);
    setOpen(false);
  };

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={wrapRef} style={{ position: "relative", minWidth: 0 }}>
      <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>{label}</div>
      <input
        className="input"
        value={query}
        onChange={handleChange}
        onFocus={() => query.trim() && results.length && setOpen(true)}
        placeholder="Type to search vendors…"
        autoComplete="off"
      />
      {loading && (
        <div style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 12, opacity: 0.5, pointerEvents: "none" }}>
          …
        </div>
      )}
      {open && results.length > 0 && (
        <div style={{
          position: "absolute", zIndex: 200, top: "100%", left: 0, right: 0,
          background: "var(--card-bg)", border: "1px solid var(--card-border)",
          borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
          maxHeight: 260, overflowY: "auto", marginTop: 2,
        }}>
          {results.map((row) => {
            const name   = row.vendor_name || row.name || row["ven_"] || "(no name)";
            const detail = [row.city, row.province || row.st, row.country].filter(Boolean).join(", ");
            return (
              <div
                key={row.id}
                onMouseDown={() => select(row)}
                style={{ padding: "10px 14px", cursor: "pointer", borderBottom: "1px solid var(--card-border)" }}
                onMouseEnter={e => e.currentTarget.style.background = "var(--btn-outline)"}
                onMouseLeave={e => e.currentTarget.style.background = ""}
              >
                <div style={{ fontWeight: 700, fontSize: 14 }}>{name}</div>
                {detail && <div className="muted" style={{ fontSize: 12 }}>{detail}</div>}
              </div>
            );
          })}
        </div>
      )}
      {open && !loading && results.length === 0 && query.trim().length >= 2 && (
        <div style={{
          position: "absolute", zIndex: 200, top: "100%", left: 0, right: 0,
          background: "var(--card-bg)", border: "1px solid var(--card-border)",
          borderRadius: 8, padding: "10px 14px", marginTop: 2,
        }}>
          <span className="muted" style={{ fontSize: 13 }}>No vendors found</span>
        </div>
      )}
    </div>
  );
}
